import { useState } from 'react';
import type { ApprovalStatus, Board } from '@/lib/api/types';
import { useBoardDefaults, useSetBoardDefaults, useTags } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { usePermission } from '@/features/workspace/usePermission';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { Select } from '@/components/Input';
import { APPROVAL_TONE, Spinner } from '@/components/Misc';
import { toast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';

export const BOARD_DEFAULT_TAGS_MAX = 20;
const APPROVALS: ApprovalStatus[] = ['none', 'in_progress', 'needs_review', 'approved'];

type Draft = { tag_ids: string[]; approval_status: ApprovalStatus | null };

/**
 * Board settings › Defaults (Release 3 META-2): tags added and an approval status set when an asset enters this board
 * (upload, add, move, copy, guest upload). Sub-boards inherit: tags are the union over the ancestors, the approval the
 * nearest board's. Existing assets are never rewritten. A default of "Approved" is gated by required fields: an
 * incomplete asset enters as "Needs review" instead and is approved by hand once its fields are filled.
 */
export function BoardDefaultsDialog({ board, onClose }: { board: Board; onClose: () => void }) {
  const wsId = useSessionStore((s) => s.workspace?.id);
  const tags = useTags(wsId);
  const defaults = useBoardDefaults(board.id);
  const canManage = usePermission('MANAGE_BOARD', { boardId: board.id });
  const save = useSetBoardDefaults(board.id);
  const [draft, setDraft] = useState<Draft | null>(null);
  const smart = board.kind === 'smart';
  const own: Draft = defaults.data?.own ?? { tag_ids: [], approval_status: null };
  const current = draft ?? own;
  const userTags = (tags.data ?? []).filter((t) => t.source === 'user').sort((a, b) => a.name.localeCompare(b.name));
  const tagName = (id: string) => userTags.find((t) => t.id === id)?.name ?? (tags.data ?? []).find((t) => t.id === id)?.name ?? 'tag';
  const boardName = (id: string | null | undefined) => (id ? ((board.breadcrumbs ?? []).find((b) => b.id === id)?.name ?? 'a parent board') : null);
  const inheritedTags = (defaults.data?.effective.tag_ids ?? []).filter((id) => !own.tag_ids.includes(id));
  const inheritedApproval = defaults.data && defaults.data.source_board_id && defaults.data.source_board_id !== board.id ? defaults.data.effective.approval_status : null;
  const toggleTag = (id: string, on: boolean) => {
    const next = on ? [...current.tag_ids, id] : current.tag_ids.filter((t) => t !== id);
    if (next.length > BOARD_DEFAULT_TAGS_MAX) return toast({ kind: 'info', title: `At most ${BOARD_DEFAULT_TAGS_MAX} default tags` });
    setDraft({ ...current, tag_ids: next });
  };
  const submit = () =>
    save.mutate(current, {
      onSuccess: () => {
        toast({ kind: 'success', title: `Defaults saved for “${board.name}”` });
        onClose();
      },
      onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not save defaults' }),
    });
  return (
    <Dialog
      open
      onClose={onClose}
      width="max-w-xl"
      title={`Defaults — ${board.name}`}
      footer={
        <>
          <Button onClick={onClose}>{canManage && !smart ? 'Cancel' : 'Close'}</Button>
          {canManage && !smart && (
            <Button variant="primary" onClick={submit} disabled={save.isPending || !draft} data-save-board-defaults>
              Save
            </Button>
          )}
        </>
      }
    >
      {smart ? (
        <p className="text-sm text-fg-muted">Smart boards hold no assets of their own, so they have no defaults.</p>
      ) : tags.isPending || defaults.isPending ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm" data-board-defaults-dialog>
          <p className="text-xs text-fg-subtle">Applied once, when an asset enters this board or a sub-board (upload, add, move, copy, guest upload). Assets already here are not changed.</p>
          <section>
            <h3 className="mb-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase">Default tags</h3>
            {userTags.length === 0 ? (
              <p className="text-xs text-fg-muted">This workspace has no tags yet.</p>
            ) : (
              <ul className="flex max-h-48 flex-wrap gap-1 overflow-y-auto" role="list">
                {userTags.map((t) => {
                  const on = current.tag_ids.includes(t.id);
                  const inherited = inheritedTags.includes(t.id);
                  return (
                    <li key={t.id}>
                      <label className={`inline-flex cursor-pointer items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${on ? 'border-accent bg-accent-soft' : inherited ? 'border-dashed border-line text-fg-muted' : 'border-line'}`} title={inherited && !on ? 'Inherited from a parent board' : undefined}>
                        <input type="checkbox" className="sr-only" checked={on} disabled={!canManage} onChange={(e) => toggleTag(t.id, e.target.checked)} aria-label={`Default tag ${t.name}`} data-default-tag={t.id} />
                        {t.name}
                        {inherited && !on && <span className="text-[10px]">· inherited</span>}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            {inheritedTags.length > 0 && <p className="mt-1 text-[11px] text-fg-subtle">Also from parent boards: {inheritedTags.map(tagName).join(', ')}.</p>}
          </section>
          <section>
            <h3 className="mb-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase">Default approval</h3>
            <Select aria-label="Default approval" value={current.approval_status ?? ''} disabled={!canManage} onChange={(e) => setDraft({ ...current, approval_status: (e.target.value || null) as ApprovalStatus | null })} data-default-approval>
              <option value="">{inheritedApproval ? `Inherit (${APPROVAL_TONE[inheritedApproval].label} · ${boardName(defaults.data?.source_board_id)})` : 'No default (keep as uploaded)'}</option>
              {APPROVALS.map((s) => (
                <option key={s} value={s}>
                  {APPROVAL_TONE[s].label}
                </option>
              ))}
            </Select>
            <p className="mt-1 text-[11px] text-fg-subtle">Only assets that arrive without a status take the default; guest uploads already enter as “Needs review”.</p>
            {(current.approval_status ?? inheritedApproval) === 'approved' && (
              <p className="mt-1 rounded-md bg-warn/15 p-2 text-xs" data-incomplete-gate>
                <strong>Required fields gate approval.</strong> An asset missing a required field enters as “Needs review” instead of “Approved”, and is approved by hand once the fields are filled.
              </p>
            )}
          </section>
        </div>
      )}
    </Dialog>
  );
}
